import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { NotificationAnswerDraft, NotificationChannel } from '../db/UserNotification.js';
import { buildBatchFeedback } from '../server/answer-feedback.js';
import { requireAnswerableNotification } from '../server/notification-access.js';
import { processNotificationAnswer, type AnswerOutcome } from '../server/processNotificationAnswer.js';

/**
 * Answer all open questions of one request in a single pass — the way the request view
 * presents them.
 *
 * Sequential, not parallel: each answer revalidates the extraction and re-syncs the
 * request's questions, and two of those running over the same document at once would race.
 * The last one to finish is also the one that finds the offer complete and hands it to SAP,
 * which is exactly the intended order.
 */
export const answerRequestNotifications = registerMutation({
  type: () => String,
  operationName: 'answerRequestNotifications',
  resolve: (mailID: string, answers: NotificationAnswerDraft[]) => {
    return async () => {
      const wanted = answers.filter(answer => answer.optionKey ?? answer.freeText?.trim() ?? answer.reference);
      if (!wanted.length)
        throw new ExpectedError('Bitte mindestens eine Frage beantworten.', {
          translationKey: 'notification.empty_answer',
        });

      const outcomes: AnswerOutcome[] = [];
      for (const answer of wanted) {
        // eslint-disable-next-line no-await-in-loop
        const handle = await requireAnswerableNotification(answer.notificationID);
        // A question from another request has no business in this request's form — it would
        // mean the client mixed up two mails, and answering it here would be invisible work.
        if (handle.notification.mailID !== mailID)
          throw new ExpectedError('Die Meldung gehört nicht zu dieser Anfrage.', {
            translationKey: 'notification.wrong_request',
          });

        // eslint-disable-next-line no-await-in-loop
        const outcome = await processNotificationAnswer(handle, {
          optionKey: answer.optionKey ?? null,
          freeText: answer.freeText ?? null,
          reference: answer.reference ?? null,
          channel: NotificationChannel.DASHBOARD,
          answeredByUserID: handle.userID,
        });
        outcomes.push(outcome);
      }

      return buildBatchFeedback(outcomes);
    };
  },
  params: [
    { name: 'mailID', type: () => String },
    { name: 'answers', type: () => [NotificationAnswerDraft] },
  ],
});
